/**
 * ─── Project color bridge ─────────────────────────────────────────────────────
 *
 * Tiny module-level pub/sub carrying the currently-shown PROJECTS item's color
 * (from workList.json) to the out-of-tree Shimmering Prism engine, which tints its
 * central glow with it. Mirrors homeSection.js (stored value + getter + listener Set)
 * rather than the reset-on-exit labBackground — the color must PERSIST across the
 * whole site, changing only when a new project becomes the shown one (or on reload,
 * when this module re-initialises to null and the engine falls back to its default).
 *
 *   setProjectColor(hex)       publish a new color (hex string, e.g. '#9F8161'); deduped + null-safe.
 *   getProjectColor()          the current color, or null before any project has been shown.
 *   subscribeProjectColor(fn)  subscribe (fn receives the hex); returns an unsubscribe function.
 *
 * Published by Work/WorkSelection when the previewed project changes; consumed by
 * ShimmeringBackground → ShimmeringPrism.setLightColor. No listeners in 'legacy' mode
 * or on mobile (the shimmer engine isn't mounted) → the publisher simply no-ops.
 * ──────────────────────────────────────────────────────────────────────────── */

let currentColor = null;        // hex string; null = engine keeps its default (white / neutral tint)
const listeners = new Set();

export function setProjectColor(hex) {
    if (!hex || currentColor === hex) return; // ignore empty + dedupe (re-hovering the same item)
    currentColor = hex;
    listeners.forEach((fn) => fn(currentColor));
}

export function getProjectColor() {
    return currentColor;
}

export function subscribeProjectColor(fn) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

/**
 * Brightness-normalize a project hex to a legible CSS rgb() string for UI cues
 * (e.g. the PROJECTS color chip). Mirrors ShimmeringPrism.setLightColor: the
 * workList colors are mostly very dark, so a raw swatch would read as near-black.
 * Normalizing to the brightest channel (max → 1) applies the project's HUE at full
 * luminance — so the small UI mark matches the perceived background tint and stays
 * visible on the dark UI. Returns null for falsy/invalid input.
 *
 * @param {string} hex '#RGB' or '#RRGGBB'
 * @returns {string|null} 'rgb(r, g, b)' or null
 */
export function normalizeProjectColor(hex) {
    if (!hex || typeof hex !== 'string') return null;
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
    let r = parseInt(h.slice(0, 2), 16);
    let g = parseInt(h.slice(2, 4), 16);
    let b = parseInt(h.slice(4, 6), 16);
    const m = Math.max(r, g, b);
    if (m > 0) {
        r = Math.round((r / m) * 255);
        g = Math.round((g / m) * 255);
        b = Math.round((b / m) * 255);
    }
    return `rgb(${r}, ${g}, ${b})`;
}
